import { call, put, takeLatest } from "redux-saga/effects";
import { bienLaiThuApi } from "../../../api/bien-lai/bienLaiThuApi";
import { sinhVienApi } from "../../../api/category/sinhVienApi";
import { mucThuKhacSinhVienApi } from "../../../api/chung-tu/mucThuKhacSinhVienApi";
import { IMucThuKhacSinhVienSelectRequest } from "../../../model/request/chung-tu/IMucThuKhacSinhVienSelectRequest";
import { IStuHoSoSinhVienViewModel } from "../../../model/respone/category/IStuHoSoSinhVienViewModel";
import { IMucThuKhacSinhVienViewModel } from "../../../model/respone/chung-tu/IMucThuKhacSinhVienViewModel";
import { IBaseRespone } from "../../../model/respone/IBaseRespone";
import { NotifyHelper } from "../../../helpers/toast";
import { eThuPhiActionIds } from "../../action-types/thu-phi/IThuPhiActionTypes";
import { rootActions } from "../../actions/rootActions";
import { IBienLaiThuCreateRequest } from "./../../../model/request/bien-lai/IBienLaiThuCreateRequest";
import { IBienLaiThuUpdateRequest } from "../../../model/request/bien-lai/IBienLaiThuUpdateRequest";
import { IThuPhiActionTypes } from "./../../action-types/thu-phi/IThuPhiActionTypes";

export function* thuPhiSaga(): any {
  yield takeLatest(eThuPhiActionIds.LOAD_MUC_THU_KHAC_START, loadWorker);
  yield takeLatest(eThuPhiActionIds.ADD_NEW_TAB_BY_ID, addNewTabWorker);
  yield takeLatest(
    eThuPhiActionIds.CREATE_BIENLAITHU_START,
    createBienLaiWorker,
  );
  yield takeLatest(
    eThuPhiActionIds.LOAD_EDIT_BIENLAI_START,
    loadEditBienLaiWorker,
  );
  yield takeLatest(
    eThuPhiActionIds.UPDATE_BIENLAITHU_START,
    updateBienLaiWorker,
  );
  yield takeLatest(
    eThuPhiActionIds.PREVIEW_BIENLAITHU_START,
    previewBienLaiWorker,
  );
}

function* loadEditBienLaiWorker(action: IThuPhiActionTypes): any {
  const { id_bien_lai, request } = action.payload as {
    id_bien_lai: number;
    request: IMucThuKhacSinhVienSelectRequest;
  };
  // Nạp chi tiết biên lai TRƯỚC để biết thu/chi + danh sách biên lai nguồn (phiếu chi).
  const resBl: IBaseRespone = yield call(
    bienLaiThuApi.SelectByIDBienLai,
    id_bien_lai,
  );
  if (!resBl.is_success) {
    yield put(
      rootActions.thuPhi.thuPhi.LOAD_EDIT_BIENLAI_ERROR({
        request,
        data: resBl.message ?? "Có lỗi",
      }),
    );
    return;
  }
  const detail = resBl.data;

  if (detail?.thu_chi) {
    // Phiếu CHI: nạp khoản theo các biên lai NGUỒN (giống loadMultiSource) — luôn dùng đường "gộp nhiều nguồn"
    // (kể cả 1 nguồn) để thống nhất: encode id duy nhất per (khoản, nguồn) + tag chi_tu_id_bien_lai.
    const sourceIds: number[] = (detail.chi_nguon_bien_lai_ids ?? []).filter(
      (x: number) => x > 0,
    );
    const merged: IMucThuKhacSinhVienViewModel[] = [];
    if (sourceIds.length) {
      const res: IBaseRespone = yield call(
        mucThuKhacSinhVienApi.SelectKhoanThuSinhVienMulti,
        {
          hoc_ky: request.hoc_ky,
          nam_hoc: request.nam_hoc,
          id_sv: request.id_sv,
          id_bien_lais: sourceIds,
          dot: -1,
        } as IMucThuKhacSinhVienSelectRequest,
      );
      if (res.is_success) {
        const groups = (res.data ?? []) as IMucThuKhacSinhVienViewModel[][];
        groups.forEach((khoans, i) => {
          const src = sourceIds[i];
          (khoans ?? []).forEach((k) => {
            const realId = k.id;
            merged.push({
              ...k,
              id: realId * 1000 + (i + 1),
              id_muc_thu_goc: realId,
              chi_tu_id_bien_lai: src,
              lop_tin_chis: k.lop_tin_chis?.map((ltc) => ({
                ...ltc,
                id_lop_tc: (ltc.id_lop_tc ?? 0) * 1000 + (i + 1),
              })),
            });
          });
        });
      } else {
        // Cùng lý do với nhánh phiếu THU bên dưới: im lặng thì lưới trống mà không rõ vì sao.
        NotifyHelper.Error(
          res.message ?? "Không lấy được khoản thu của phiếu nguồn",
        );
      }
    }
    yield put(
      rootActions.thuPhi.thuPhi.LOAD_MUC_THU_KHAC_SUCCESS({
        data: merged,
        request: { ...request, dot: -1 },
      }),
    );
    yield put(
      rootActions.thuPhi.thuPhi.LOAD_EDIT_BIENLAI_SUCCESS({
        request,
        data: detail,
      }),
    );
    return;
  }

  /*
    Phiếu THU: nạp khoản theo CHÍNH BIÊN LAI ĐANG SỬA, không theo bộ lọc đang chọn trên màn hình.
    ⚠ Trước đây dùng nguyên `request` của màn (kỳ/năm đang chọn + dot = 0) nên phiếu nào có khoản
    thuộc ĐỢT KHÁC 0 là lưới khoản TRỐNG: thủ tục lọc `Dot = @dot`. (HVTC 21/08/2026, phiếu 76643
    — khoản "Học phí gia hạn" nằm ở đợt 8, mở sửa ra lưới rỗng.)
    Máy nào bật cấu hình "thu tất cả học kỳ/năm học" thì máy chủ tự bỏ lọc kỳ/năm nên vớ được một
    khoản khác CÙNG LOẠI THU (sai khoản mà nhìn như chạy được) — đúng cảnh "dưới máy chạy được,
    lên máy chủ thì trống".
    dot = -1: lấy đủ mọi đợt rồi để giao diện tự khớp khoản của phiếu.
  */
  const requestTheoPhieu: IMucThuKhacSinhVienSelectRequest = {
    ...request,
    hoc_ky: detail?.hoc_ky ?? request.hoc_ky,
    nam_hoc: detail?.nam_hoc ?? request.nam_hoc,
    dot: -1,
  };
  const resMuc: IBaseRespone = yield call(
    mucThuKhacSinhVienApi.SelectKhoanThuSinhVien,
    requestTheoPhieu,
  );
  if (resMuc.is_success) {
    yield put(
      rootActions.thuPhi.thuPhi.LOAD_MUC_THU_KHAC_SUCCESS({
        request,
        data: resMuc.data,
      }),
    );
  } else {
    /*
      ⚠ HỎNG THÌ PHẢI NÓI RA. Trước đây chỉ có nhánh thành công, gọi lỗi là im lặng bỏ qua ->
      màn sửa phiếu mở lên với LƯỚI KHOẢN NỘP TRỐNG mà không một dòng thông báo, người dùng tưởng
      phiếu mất khoản. Hay gặp nhất khi BẬT PHẠM VI DỮ LIỆU: máy chủ trả "Biên lai này / Sinh viên
      này không thuộc phạm vi dữ liệu của bạn" — đúng thứ cần hiện lên để biết đường xử lý
      (chạy dưới máy không bật phạm vi nên không bao giờ thấy).
    */
    NotifyHelper.Error(resMuc.message ?? "Không lấy được khoản thu của phiếu");
  }
  yield put(
    rootActions.thuPhi.thuPhi.LOAD_EDIT_BIENLAI_SUCCESS({
      request,
      data: detail,
    }),
  );
}

function* updateBienLaiWorker(action: IThuPhiActionTypes): any {
  const request = action.payload as IBienLaiThuUpdateRequest;
  const res: IBaseRespone = yield call(bienLaiThuApi.Update, request);
  if (res.is_success) {
    const data: number = res.data;
    yield put(rootActions.bienLai.bienLaiViewChiTiet.LOAD_START(data));
    yield put(
      rootActions.bienLai.bienLaiView.LOAD_START({
        hoc_ky: request.hoc_ky,
        nam_hoc: request.nam_hoc,
        id_sv: request.id_sv,
      }),
    );
    yield put(
      rootActions.bienLai.bienLaiView.LOAD_START({
        hoc_ky: 0,
        nam_hoc: "",
        id_sv: request.id_sv,
      }),
    );
    yield put(
      rootActions.thuPhi.thuPhi.UPDATE_BIENLAITHU_SUCCESS({
        request: { ...request, dot: 0 },
        data,
      }),
    );
    yield put(
      rootActions.report.sinhVien.LOAD_LUY_KE_SO_LIEU_START(request.id_sv),
    );
  } else {
    yield put(
      rootActions.thuPhi.thuPhi.UPDATE_BIENLAITHU_ERROR({
        request: { ...request, dot: 0 },
        data: res.message ?? "Có lỗi",
      }),
    );
  }
}

function* loadWorker(action: IThuPhiActionTypes): any {
  const request = action.payload as IMucThuKhacSinhVienSelectRequest;
  const res: IBaseRespone = yield call(
    mucThuKhacSinhVienApi.SelectKhoanThuSinhVien,
    request,
  );
  if (res.is_success) {
    const data: IMucThuKhacSinhVienViewModel[] = res.data;
    yield put(
      rootActions.thuPhi.thuPhi.LOAD_MUC_THU_KHAC_SUCCESS({
        request: request,
        data,
      }),
    );
  } else {
    yield put(
      rootActions.thuPhi.thuPhi.LOAD_MUC_THU_KHAC_ERROR({
        request: request,
        data: res.message ?? "Có lỗi",
      }),
    );
  }
}

function* createBienLaiWorker(action: IThuPhiActionTypes): any {
  const request = action.payload as IBienLaiThuCreateRequest;
  console.log(request);
  const res: IBaseRespone = yield call(bienLaiThuApi.Create, request);
  if (res.is_success) {
    const data: number = res.data;
    yield put(rootActions.bienLai.bienLaiViewChiTiet.LOAD_START(data));
    yield put(
      rootActions.bienLai.bienLaiView.LOAD_START({
        hoc_ky: request.hoc_ky,
        nam_hoc: request.nam_hoc,
        id_sv: request.id_sv,
      }),
    );
    // Làm mới luôn danh sách "Tất cả" (mọi học kỳ, mọi năm) để khớp biên lai vừa tạo
    yield put(
      rootActions.bienLai.bienLaiView.LOAD_START({
        hoc_ky: 0,
        nam_hoc: "",
        id_sv: request.id_sv,
      }),
    );

    yield put(
      rootActions.thuPhi.thuPhi.CREATE_BIENLAITHU_SUCCESS({
        request: {
          ...request,
          dot: 0,
        },
        data,
      }),
    );
    yield put(
      rootActions.report.sinhVien.LOAD_LUY_KE_SO_LIEU_START(request.id_sv),
    );
    // yield put(
    //   rootActions.mucThu.mucthuTamThu.LOAD_SO_TIEN_CON_LAI_LOAD_START(
    //     request.id_sv
    //   )
    // );
  } else {
    yield put(
      rootActions.thuPhi.thuPhi.CREATE_BIENLAITHU_ERROR({
        request: {
          ...request,
          dot: 0,
        },
        data: res.message ?? "Có lỗi",
      }),
    );
  }
}

function* addNewTabWorker(action: IThuPhiActionTypes): any {
  const request = action.payload as string;
  const res: IBaseRespone = yield call(sinhVienApi.SelectById, request);
  if (res.is_success) {
    const data: IStuHoSoSinhVienViewModel = res.data;
    yield put(rootActions.thuPhi.thuPhi.ADD_NEW_TAB(data));
  } else {
    yield put(
      rootActions.thuPhi.thuPhi.ADD_NEW_TAB_BY_ID_ERROR(
        res.message ?? "Có lỗi",
      ),
    );
  }
}

function* previewBienLaiWorker(action: IThuPhiActionTypes): any {
  const request = action.payload as IBienLaiThuCreateRequest;
  const res: IBaseRespone = yield call(bienLaiThuApi.Preview, request);
  if (res.is_success) {
    const data: string = res.data;
    yield put(
      rootActions.thuPhi.thuPhi.PREVIEW_BIENLAITHU_SUCCESS({
        request: {
          ...request,
          dot: 0,
        },
        data,
      }),
    );
  } else {
    yield put(
      rootActions.thuPhi.thuPhi.PREVIEW_BIENLAITHU_ERROR({
        request: {
          ...request,
          dot: 0,
        },
        data: res.message ?? "Có lỗi",
      }),
    );
  }
}
